import { resolveSchema } from './workflowSpec.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'

const findCardTypeByRole = (cardTypes, role) => cardTypes.find((ct) => ct.domainModelRole === role)

const findAggregateRootCard = (event, cardTypesMap) => {
  const cards = event.cards || []
  for (let j = 0; j < cards.length; j++) {
    const card = cards[j]
    const cardType = cardTypesMap[card.cardTypeId]
    if (cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
      return { card, cardIndex: j }
    }
  }

  return null
}

const findGwtCards = (event, cardTypesMap) => {
  const results = []
  const cards = event.cards || []
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i]
    const cardType = cardTypesMap[card.cardTypeId]
    if (cardType?.domainModelRole === DomainModelRole.GIVEN_WHEN_THEN) {
      results.push({ card, cardIndex: i })
    }
  }

  return results
}

// Builds the card operations for update_domain_event's aggregateRoot / acceptanceCriteria params:
// aggregateRoot upserts (or removes, on empty string) the event's AggregateRoot card; acceptanceCriteria
// replaces ALL Given-When-Then cards. Persistence-free — the caller runs the deletes (descending index),
// then the updates, then the new cards, re-threading the workflow version between steps.
export const buildUpdateEventCardOperations = ({ workflow, event, aggregateRoot, acceptanceCriteria, cardTypes }) => {
  const domainEvents = workflow.domainEvents || []
  const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))

  const pendingNewCards = []
  const pendingCardDeletions = []
  const pendingCardUpdates = []
  const changes = []

  if (aggregateRoot !== undefined) {
    const aggregateRootCardType = findCardTypeByRole(cardTypes || [], DomainModelRole.AGGREGATE_ROOT)

    if (!aggregateRootCardType) {
      return { error: 'Aggregate root card type not found in this workflow' }
    }

    const existingAggregateRoot = findAggregateRootCard(event, cardTypesMap)

    if (aggregateRoot === '') {
      if (existingAggregateRoot) {
        pendingCardDeletions.push({
          cardId: existingAggregateRoot.card.id,
          cardIndex: existingAggregateRoot.cardIndex,
          cardDescription: existingAggregateRoot.card.description
        })
        changes.push('aggregate root removed')
      }
    } else {
      const resolvedEntity = resolveSchema(aggregateRoot, workflow)
      if (!resolvedEntity) {
        return { error: `Entity not found for ref: ${aggregateRoot}` }
      }

      const entitySchema = (workflow.schemas || []).find((s) => s.id === resolvedEntity.id)
      if (!entitySchema || entitySchema.type !== 'Entity') {
        return { error: `"${aggregateRoot}" is not an Entity schema` }
      }

      const boundedContextId = entitySchema.boundedContext || null

      if (existingAggregateRoot) {
        pendingCardUpdates.push({
          cardId: existingAggregateRoot.card.id,
          cardIndex: existingAggregateRoot.cardIndex,
          expectedVersion: existingAggregateRoot.card.version,
          description: entitySchema.name,
          sortkey: existingAggregateRoot.card.sortkey,
          cardTypeId: existingAggregateRoot.card.cardTypeId,
          done: existingAggregateRoot.card.done ?? false,
          schemaId: resolvedEntity.id,
          boundedContext: boundedContextId,
          releaseNo: existingAggregateRoot.card.releaseNo
        })
        changes.push(`aggregate root set to "${entitySchema.name}"`)
      } else {
        const lastSortkey = getLastCardSortkey(domainEvents)
        const sortkey = computeMidString(lastSortkey, '')
        pendingNewCards.push({
          description: entitySchema.name,
          done: false,
          cardTypeId: aggregateRootCardType.id,
          releaseNo: null,
          sortkey,
          boundedContext: boundedContextId,
          schemaId: resolvedEntity.id
        })
        changes.push(`aggregate root set to "${entitySchema.name}"`)
      }
    }
  }

  if (acceptanceCriteria !== undefined) {
    const givenWhenThenCardType = findCardTypeByRole(cardTypes || [], DomainModelRole.GIVEN_WHEN_THEN)

    if (!givenWhenThenCardType) {
      return { error: 'Acceptance criteria (Given-When-Then) card type not found in this workflow' }
    }

    const existingGwtCards = findGwtCards(event, cardTypesMap)

    for (const gwtCard of existingGwtCards) {
      pendingCardDeletions.push({
        cardId: gwtCard.card.id,
        cardIndex: gwtCard.cardIndex,
        cardDescription: gwtCard.card.description
      })
    }

    let lastSortkey = getLastCardSortkey(domainEvents)
    for (const text of acceptanceCriteria) {
      const sortkey = computeMidString(lastSortkey, '')
      lastSortkey = sortkey

      pendingNewCards.push({
        description: text,
        done: false,
        cardTypeId: givenWhenThenCardType.id,
        releaseNo: null,
        sortkey,
        boundedContext: null,
        schemaId: null
      })
    }

    if (acceptanceCriteria.length === 0 && existingGwtCards.length > 0) {
      changes.push(`${existingGwtCards.length} acceptance criteria removed`)
    } else if (acceptanceCriteria.length > 0) {
      changes.push(`${acceptanceCriteria.length} acceptance criteria set`)
    }
  }

  return { pendingNewCards, pendingCardDeletions, pendingCardUpdates, changes }
}
